import { ToolsService } from '../../../services/tools.service';
import { OverlayService } from '../../../services/overlay.service';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../../../services/product.service';
import { Store } from '../../../store';
import { BasketService } from '../../../services/basket.service';
import { SeoService } from '../../../services/seo.service';
import { BreadcrumbsService } from '../../../services/breadcrumbs.service';
import { GoogleAnalyticsService } from '../../tracking/services/google-analytics.service';
import { FacebookPixelService } from '../../tracking/services/facebook-pixel.service';
import { of, Subject } from 'rxjs';
import { tap, switchMap, takeUntil } from 'rxjs/operators';
import { RewardService } from '../../../services/reward.service';
import { ProductPriceEngine } from './product-price.engine';

@Component({
    selector: 'app-product-details',
    templateUrl: './product-details.component.html',
    styleUrls: ['./product-details.component.scss'],
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
    deviceType$ = this.store.select<string>('deviceType');

    deviceType: string;
    destroy$ = new Subject<void>();

    priceEngine = new ProductPriceEngine(
        this.store,
        this.productService,
        this.basketService,
        this.toolsService,
        this.rewardService
    );
    addedToBasket = false;

    products = [];

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.deviceType = this.store.selectForLocal('deviceType');
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private productService: ProductService,
        private store: Store,
        private basketService: BasketService,
        private overlayService: OverlayService,
        private seoService: SeoService,
        private toolsService: ToolsService,
        private breadcrumbsService: BreadcrumbsService,
        private gaService: GoogleAnalyticsService,
        private fbqService: FacebookPixelService,
        private rewardService: RewardService
    ) { }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    ngOnInit(): void {
        this.deviceType = this.store.selectForLocal('deviceType');

        this.activatedRoute.params
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) => {
                    if (!this.priceEngine.product || this.priceEngine.product.slug !== params.slug) {
                        return this.priceEngine.getProductDataFromSlug(params.slug);
                    }
                    return of(null);
                }),
                tap(async () => {
                    this.setMetaTags();
                    this.setBreadCrumbs();
                    this.fbqService.event('ViewContent', {
                        content_ids: [this.priceEngine.product.id],
                        content_category: this.priceEngine.product.range.name,
                        content_type: 'product',
                        content_name: this.priceEngine.product.name,
                        currency: 'GBP',
                        value: this.priceEngine.product.productDetails[0].price,
                    });

                    this.products = [];
                    const productLimit = 3;
                    for (
                        let i = 0;
                        i < this.priceEngine.product.productGroup.length && this.products.length < productLimit;
                        i++
                    ) {
                        const res = await this.productService
                            .productsByGroup(this.priceEngine.product.productGroup[i])
                            .toPromise();

                        this.products.push(...res.data.slice(0, productLimit));
                    }
                })
            )
            .subscribe();
    }

    addToBasket(isSample) {
        const itemToAdd = this.priceEngine.getBasketItem(isSample);

        this.basketService.addToBasket(JSON.parse(JSON.stringify(itemToAdd)));
        if (!isSample) {
            this.gaService.event('add_to_cart', 'Ecommerce', this.priceEngine.product.productCode, {
                ecommerce: {
                    currencyCode: 'GBP',
                    add: {
                        products: [
                            {
                                name: itemToAdd.product.name,
                                id: itemToAdd.product.id,
                                brand: 'Yorkstone Supplies',
                                skuId: itemToAdd.skuId,
                                price: itemToAdd.pricing.totals.net,
                                category: itemToAdd.product.range.name,
                                typeId: itemToAdd.product.typeId,
                                quantity: itemToAdd.qty,
                                discount: itemToAdd.totalDiscount,
                            },
                        ],
                    },
                },
            });

            this.fbqService.event('AddToCart', {
                content_name: itemToAdd.product.name,
                content_category: itemToAdd.product.range.name,
                content_ids: [itemToAdd.product.id],
                content_type: 'product',
                value: itemToAdd.pricing.totals.net,
                currency: 'GBP',
            });
        }

        window.scroll(0, 0);

        this.overlayService.toggle('addedToBasket');
        this.addedToBasket = true;
    }

    setMetaTags() {
        this.seoService.updateTitle(this.priceEngine.product.seoTitle);
        this.seoService.addMetaTag('description', this.priceEngine.product.seoDescription);
        this.seoService.createLinkForCanonicalURL();
    }

    setBreadCrumbs() {
        if (this.priceEngine.product.jobLot) {
            this.breadcrumbsService.changeBreadcrumb([
                { text: 'Marketplace', route: ['/products/marketplace'] },
                { text: this.priceEngine.product.name },
            ]);

        } else {
            this.breadcrumbsService.changeBreadcrumb([
                { text: 'Our Products', route: ['/product-groups'] },
                {
                    text: this.priceEngine.product.productGroup[0].name,
                    route: ['/', 'product-groups', this.priceEngine.product.productGroup[0].slug],
                },
                { text: this.priceEngine.product.name },
            ]);

        }
    }

    openProductDetails(slug) {
        this.router.navigate([`/products/${slug}`]);
    }
}
